import { useContext, useEffect, useState } from 'react';

import {
	Button,
	Checkbox,
	Col,
	Drawer,
	Flex,
	Form,
	Input,
	Select,
	Tabs,
	Tooltip,
	message,
} from 'antd';
import { useForm } from 'antd/es/form/Form';
import TextArea from 'antd/es/input/TextArea';

import {
	useAddMonitor,
	useGetSingleMonitorCollectionById,
	useUpdateMonitor,
} from '@/api/monitor_collections';
import PromptItem from '@/components/common/PromptItem';
import { WHISPER_ACTIONS } from '@/const';
import getFrequencyFromCron from '@/lib/helpers/getFrequencyFromCron';
import prepareIABValues from '@/lib/helpers/prepareIABValues';
import { PageContext } from '@/providers/PageContextProvider';

const layout = {
	labelCol: { span: 5 },
	wrapperCol: { span: 21 },
};

export default function EditMonitorDrawer() {
	const { drawer, closeDrawer } = useContext(PageContext);
	const [form] = useForm();
	const [tabActiveKey, setTabActiveKey] = useState('1');
	const [promptsErrIdxs, setPromptsErrIdxs] = useState<any>([]);

	// console.log('drawer?.data', drawer?.data);

	const id = drawer?.data?.id;

	const { data: singleMonitorData, isFetching: isSingleMonitorFetching } =
		useGetSingleMonitorCollectionById({
			id,
			enabled: drawer?.isOpen && drawer.type === 'edit-monitor' && !!id,
		});

	const { mutate: updateMonitor, isLoading: isUpdateLoading } =
		useUpdateMonitor();

	let item = singleMonitorData?.data?.data?.item;

	const onCancelClick = () => {
		closeDrawer();
		setTabActiveKey('1');
		form.resetFields();
		setPromptsErrIdxs([]);
	};

	const onSaveClick = async () => {
		try {
			setPromptsErrIdxs([]);
			await form.validateFields();
			const {
				title,
				description,
				active,
				frequency,
				keywords,
				prompts,
				brands,
				iab,
				// topics,
			} = form.getFieldsValue();
			updateMonitor(
				{
					id,
					title: title?.trim(),
					description: description ? description?.trim() : '',
					active,
					frequency,
					keywords,
					// topics,
					brands,
					prompts: prompts?.length
						? prompts?.map((pItem: any) => {
								return {
									title: pItem?.title?.trim(),
									prompt: pItem?.prompt?.trim(),
									trigger: pItem?.trigger || '',
									eval_schema: pItem?.eval_schema || [],
								};
						  })
						: [],
					iab_categories: iab?.length ? iab : [],
				},
				{
					onSuccess: () => {
						message.success('Updated');
						closeDrawer();
						setTabActiveKey('1');
						form.resetFields();
					},
					onError: (error) => {
						const err = error as any;
						message.error(
							`An error occured: ${
								err?.response?.data?.message || err?.message
							}.`,
						);
						console.error('error', error);
					},
				},
			);
		} catch (error) {
			console.log('error', error);
			message.error('Please fill all required fields');
			if (
				(error as any)?.errorFields?.some((item: any) =>
					item?.name?.includes('prompts'),
				)
			) {
				const promptsErrIdxs =
					[
						...(error as any)?.errorFields
							?.filter((item: any) => item?.name?.includes('prompts'))
							?.map((item: any) => item?.name[1])
							?.filter((item: any) => item !== undefined),
					].reduce((acc: any, curr: any) => {
						if (!acc.includes(curr)) {
							acc.push(curr);
						}
						return acc;
					}, []) || [];
				setPromptsErrIdxs(promptsErrIdxs);
			}
		}
	};

	useEffect(() => {
		form.resetFields();
		item &&
			form.setFieldsValue({
				title: item?.title || '',
				description: item?.description || '',
				active: item?.active ? true : false,
				frequency: getFrequencyFromCron(item?.cron),
				keywords: item?.keywords_list || [],
				brands: item?.brands_list || [],
				topics: item?.topics_list || [],
				prompts: item?.prompts_list || [],
			});
	}, [item]);

	return (
		<Drawer
			open={drawer.isOpen && drawer.type === 'edit-monitor'}
			width={'650px'}
			onClose={onCancelClick}
			title="Edit monitor"
		>
			<Col span={24}>
				<Flex
					justify="end"
					align="center"
					gap={20}
					style={{ marginBottom: '1.25rem' }}
				>
					<Button onClick={onCancelClick}>Cancel</Button>
					<Button
						type="primary"
						onClick={onSaveClick}
						loading={isSingleMonitorFetching || isUpdateLoading}
					>
						Save
					</Button>
				</Flex>
			</Col>
			<Form
				layout="vertical"
				form={form}
				initialValues={{
					title: item?.title,
					description: item?.description,
					active: item?.active ? true : false,
					prompts: item?.prompts_list,
					brands:
						item?.brands_list?.map((keyword: string) => ({
							label: keyword,
							value: keyword,
						})) || [],
					// topics:
					//   item?.topics_list?.map((keyword: string) => ({
					//     label: keyword,
					//     value: keyword,
					//   })) || [],
					keywords:
						item?.keywords_list?.map((keyword: string) => ({
							label: keyword,
							value: keyword,
						})) || [],
					frequency: getFrequencyFromCron(item?.cron),
					iab: item?.iab_categories,
				}}
				style={{ marginBottom: '1rem' }}
			>
				<Tabs
					defaultActiveKey="1"
					onChange={(key) => setTabActiveKey(key)}
					activeKey={tabActiveKey}
					style={{ padding: '0 1rem' }}
					items={[
						{
							key: '1',
							label: 'General & Meta',
							forceRender: true,
							children: (
								<Flex vertical>
									<Form.Item
										layout="vertical"
										label="Title"
										name="title"
										rules={[
											{ required: true, message: 'Title is required.' },
											{
												whitespace: true,
												message: 'Title cannot be empty.',
											},
										]}
										// initialValue={data?.name}
										// wrapperCol={{ offset: 6, span: 16 }}
										// onMetaChange={}
										// style={{ margin: 0 }}
									>
										<Input />
									</Form.Item>

									<Form.Item
										layout="vertical"
										label="Description"
										name="description"
										// initialValue={data?.description}
										// style={{ marginBottom: "3rem" }}
									>
										<TextArea rows={2} style={{ resize: 'none' }} />
									</Form.Item>

									{/* {data?.active !== undefined && ( */}
									<Tooltip>
										<Form.Item
											layout="vertical"
											// label="Active"
											name="active"
											valuePropName="checked"
											// initialValue={data?.active}
											// wrapperCol={{ offset: 6, span: 16 }}
											// onMetaChange={}

											style={{ marginBottom: '1rem' }}
										>
											{!item?.items_amount || item?.items_amount === 0 ? (
												<Tooltip title="You need to add at least one category to activate the monitor">
													<Checkbox
														disabled={
															!item?.items_amount || item?.items_amount === 0
														}
													>
														Active
													</Checkbox>
												</Tooltip>
											) : (
												<Checkbox>Active</Checkbox>
											)}
										</Form.Item>
									</Tooltip>

									<Form.Item
										label={'Brands'}
										name="brands"
										// initialValue={data?.brands}
										// style={{ marginBottom: '0.5rem' }}
									>
										<Select
											mode="tags"
											style={{ width: '100%' }}
											placeholder="Brands"
											// onChange={handleChange}
											options={[]}
											// defaultValue={[]}
										/>
									</Form.Item>
									<Form.Item
										label={'Keywords'}
										name="keywords"
										// initialValue={data?.keywords}
										// style={{ marginBottom: '0.5rem' }}
									>
										<Select
											mode="tags"
											style={{ width: '100%' }}
											placeholder="Keywords"
											// onChange={handleChange}
											options={[]}
											// defaultValue={
											//   data?.keywords?.map((keyword: string) => ({
											//     label: keyword,
											//     value: keyword,
											//   })) || []
											// }
										/>
									</Form.Item>

									<Form.Item
										label={'Frequency'}
										name="frequency"
										initialValue={'Daily'}
									>
										<Select
											style={{ width: '100%' }}
											placeholder="Frequency"
											// onChange={handleChange}
											options={[
												{ value: 'Daily', label: 'Daily' },
												{ value: 'Weekly', label: 'Weekly' },
												{ value: 'Monthly', label: 'Monthly' },
											]}
											// defaultValue="Daily"
										/>
									</Form.Item>
									{/* )} */}
								</Flex>
							),
						},
						// {
						// 	key: '2',
						// 	label: 'Meta',
						// 	forceRender: true,
						// 	children: (
						// 		<Flex vertical gap={8}>
						// 			<Form.Item
						// 				label={'Brands'}
						// 				name="brands"
						// 				// initialValue={data?.brands}
						// 				style={{ marginBottom: '0.5rem' }}
						// 			>
						// 				<Select
						// 					mode="tags"
						// 					style={{ width: '100%' }}
						// 					placeholder="Brands"
						// 					// onChange={handleChange}
						// 					options={[]}
						// 					// defaultValue={[]}
						// 				/>
						// 			</Form.Item>
						// 			<Form.Item
						// 				label={'Keywords'}
						// 				name="keywords"
						// 				// initialValue={data?.keywords}
						// 				style={{ marginBottom: '0.5rem' }}
						// 			>
						// 				<Select
						// 					mode="tags"
						// 					style={{ width: '100%' }}
						// 					placeholder="Keywords"
						// 					// onChange={handleChange}
						// 					options={[]}
						// 					// defaultValue={
						// 					//   data?.keywords?.map((keyword: string) => ({
						// 					//     label: keyword,
						// 					//     value: keyword,
						// 					//   })) || []
						// 					// }
						// 				/>
						// 			</Form.Item>

						// 			<Form.Item
						// 				label={'Frequency'}
						// 				name="frequency"
						// 				initialValue={'Daily'}
						// 			>
						// 				<Select
						// 					style={{ width: '100%' }}
						// 					placeholder="Frequency"
						// 					// onChange={handleChange}
						// 					options={[
						// 						{ value: 'Daily', label: 'Daily' },
						// 						{ value: 'Weekly', label: 'Weekly' },
						// 						{ value: 'Monthly', label: 'Monthly' },
						// 					]}
						// 					// defaultValue="Daily"
						// 				/>
						// 			</Form.Item>
						// 		</Flex>
						// 	),
						// },
						{
							key: '3',
							label: 'Prompts',
							forceRender: true,
							children: (
								<Flex vertical gap={8} align="stretch">
									<Form.List name="prompts">
										{(fields, { add, remove }) => (
											<>
												{fields.map(({ key, name, ...restField }) => (
													<PromptItem
														keyProp={key}
														name={name}
														restField={restField}
														deleteDisabled={false}
														hasError={promptsErrIdxs?.includes(name)}
														remove={remove}
													/>
												))}
												<Form.Item
													style={{ display: 'flex', justifyContent: 'center' }}
												>
													<Button
														type="dashed"
														onClick={() => add()}
														disabled={fields.length >= 3}
													>
														Add Prompt
													</Button>
												</Form.Item>
											</>
										)}
									</Form.List>
								</Flex>
							),
						},
					]}
				></Tabs>
			</Form>
		</Drawer>
	);
}
